/**
 * "AI ดูแลแชท (ทดสอบ)" on the chat screen (docs/ai-mode.md): may the AI take this chat, the open session and what
 * it understood (side card), turning it on/off, "รับต่อ" after a hand-off, and the in-app alert when the AI hands
 * a chat to me. The row/header state itself (`ai_mode`, `ai_handoff`) comes with every ConversationSummary.
 */
import { defineStore } from 'pinia'
import { ref } from 'vue'
import { getAiModeApi } from '../api/aimode'
import type { AiModeEvent, AiModeView } from '../api/aimode/types'
import type { AiBusy } from '../api/types'
import { applyBusy, bubbleLanded, busyNow, type BusyLive } from '../lib/aiBusy'
import { chime, installUnlock, PlayedLog, setSoundEnabled, SOUND_KEY, soundEnabled, type Chime } from '../lib/chime'
import { useClock } from './clock'
import { useInbox } from './inbox'

export interface HandoffAlert {
  key: string
  conversationId: string
  code: string
  note: string
  at: number
}

const ALERT_MAX = 3

export const useAiMode = defineStore('aimode', () => {
  const views = ref<Record<string, AiModeView>>({})
  const busy = ref<Record<string, boolean>>({})
  const alerts = ref<HandoffAlert[]>([])
  /** The side card is open unless the staff folded it (per browser tab). */
  const cardOpen = ref(false) // owner 28 ก.ย. 23:15: "ย่อไว้ก่อนเสมอ ถ้าอยากดูเดี๋ยวกดดูเอง" (AiModeCard folds again per chat)
  /** "เสียงเตือน" — chime when the AI hands a chat to me (per person, per browser, default on). lib/chime.ts */
  const soundOn = ref(soundEnabled())
  let sound: Chime = chime
  let played = new PlayedLog()
  installUnlock()
  if (typeof window !== 'undefined') {
    // switched in another tab of this browser: follow it here too
    window.addEventListener('storage', (e) => {
      if (e.key === SOUND_KEY) soundOn.value = soundEnabled()
    })
  }

  function setSound(on: boolean): void {
    soundOn.value = on
    setSoundEnabled(on)
    if (on) sound.unlock() // turning it on is a click: the browser allows audio from here
    useInbox().flash(on ? 'เปิดเสียงเตือนแล้ว' : 'ปิดเสียงเตือนแล้ว · เปิดอีกครั้งได้ที่เมนูบัญชี มุมล่างซ้าย')
  }

  /** Chime for a hand-off shown to me — once per hand-off, never when switched off. true = it rang. */
  function ring(key: string): boolean {
    if (!soundOn.value) {
      played.mark(key) // switched on later: an old hand-off must not ring then
      return false
    }
    return played.claim(key) === 'play' && sound.play()
  }

  /** Tests: a fake chime / storage. */
  function useSound(c: Chime, log: PlayedLog): void {
    sound = c
    played = log
  }

  const errText = (e: unknown): string => (e instanceof Error ? e.message : String(e))
  const seq: Record<string, number> = {}

  async function load(id: string): Promise<void> {
    const n = (seq[id] = (seq[id] ?? 0) + 1)
    try {
      const v = await getAiModeApi().view(id)
      if (n === seq[id]) views.value[id] = v
    } catch {
      /* the header then shows the button disabled until the next try */
    }
  }

  async function setOn(id: string, on: boolean): Promise<boolean> {
    if (busy.value[id]) return false
    busy.value[id] = true
    const inbox = useInbox()
    try {
      const conv = await getAiModeApi().set(id, on)
      inbox.applyEvent({ type: 'conversation.updated', conversation: conv })
      inbox.flash(on ? 'ให้ AI ดูแลแชทนี้แล้ว' : 'หยุด AI แล้ว แชทกลับมาให้พนักงานตอบ')
      void load(id)
      return true
    } catch (e) {
      inbox.flash(errText(e))
      void load(id)
      return false
    } finally {
      busy.value[id] = false
    }
  }

  /** "รับต่อ": the warning goes away and the chat is mine. */
  async function accept(id: string): Promise<void> {
    if (busy.value[id]) return
    busy.value[id] = true
    const inbox = useInbox()
    try {
      const conv = await getAiModeApi().acceptHandoff(id)
      inbox.applyEvent({ type: 'conversation.updated', conversation: conv })
      dismissFor(id)
    } catch (e) {
      inbox.flash(errText(e))
    } finally {
      busy.value[id] = false
    }
  }

  /**
   * "รับเรื่องเอง" on a row of the AI tab: the AI stops in this chat (same as "หยุด AI") and the chat becomes mine,
   * so I answer next. The row then lives in กำลังคุย.
   */
  async function takeOver(id: string): Promise<boolean> {
    if (busy.value[id]) return false
    busy.value[id] = true
    const inbox = useInbox()
    try {
      const conv = await getAiModeApi().set(id, false)
      inbox.applyEvent({ type: 'conversation.updated', conversation: conv })
      const me = inbox.meId
      if (me && conv.assigned_staff?.id !== me) await inbox.reassign(id, me)
      inbox.flash('รับเรื่องแล้ว AI หยุดตอบแชทนี้')
      void load(id)
      return true
    } catch (e) {
      inbox.flash(errText(e))
      return false
    } finally {
      busy.value[id] = false
    }
  }

  function dismiss(key: string): void {
    alerts.value = alerts.value.filter((a) => a.key !== key)
  }
  function dismissFor(convId: string): void {
    alerts.value = alerts.value.filter((a) => a.conversationId !== convId)
  }

  // ------------------------------------------------ "AI กำลังคิด / กำลังพิมพ์" (lib/aiBusy.ts)
  /** Live state per chat from WS ai.busy (ephemeral; the page-load value is ConversationSummary.ai_mode.busy). */
  const activity = ref<Record<string, BusyLive>>({})

  /** What the AI is doing in this chat right now (only while it is in charge). Ticks with the shared clock. */
  function busyOf(convId: string): AiBusy | null {
    const mode = useInbox().convs[convId]?.ai_mode
    if (!mode) return null
    return busyNow(activity.value[convId], mode.busy, useClock().now)
  }

  /** The AI's bubble landed (inbox realtime): stop holding "กำลังพิมพ์" for it. */
  function onAiBubble(convId: string): void {
    const cur = activity.value[convId]
    const next = bubbleLanded(cur)
    if (next && next !== cur) activity.value[convId] = next
  }

  /** Socket events (api/app/ai/aimode_runner.py): ai.turn → refresh the open chat's card · ai.handoff → alert me ·
   *  ai.busy → the "AI กำลังคิด" row. */
  function onEvent(e: AiModeEvent): void {
    const inbox = useInbox()
    if (e.type === 'ai.busy') {
      const next = applyBusy(activity.value[e.conversation_id], e, useClock().now)
      if (next) activity.value[e.conversation_id] = next
      return
    }
    if (e.conversation_id in views.value) void load(e.conversation_id)
    if (e.type !== 'ai.handoff') return
    const mine = !e.staff_id || e.staff_id === inbox.meId
    if (!mine) return
    const key = `${e.conversation_id}:${e.session_id}`
    if (alerts.value.some((a) => a.key === key)) return
    alerts.value = [{ key, conversationId: e.conversation_id, code: e.code, note: e.note, at: Date.now() }, ...alerts.value].slice(
      0,
      ALERT_MAX,
    )
    ring(key)
  }

  return {
    views, busy, alerts, cardOpen, soundOn, activity,
    load, setOn, accept, takeOver, dismiss, dismissFor, onEvent, setSound, ring, useSound, busyOf, onAiBubble,
  }
})
